import { ApolloError } from '@apollo/client/errors';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { injectAutoHideToastOfType } from '../../../utils/Toast';
import {
  DISABLE_USER_CONFIRMATION_DESCRIPTION,
  DISABLE_USER_CONFIRMATION_TITLE,
  ENABLE_USER_CONFIRMATION,
  MEMBER_DISABLED_SUCCESS_MESSAGE,
  MEMBER_ENABLED_SUCCESS_MESSAGE,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../../utils/constants';
import { DISABLE_USER, ENABLE_USER } from '../../../apollo/mutations';
import { Member } from '../../../apollo/queries';

export enum MemberActionsEnum {
  ENABLE_USER = 'ENABLE_USER',
  DISABLE_USER = 'DISABLE_USER',
}

interface Props {
  setActive(active: boolean): void;
  member: Member;
}

const useMemberActions = ({ setActive, member }: Props) => {
  const { id: memberId, firstName, name } = member;
  const MEMBER_PAGE_TOAST_ID = 'members-page-toast';
  const [selectedAction, setSelectedAction] = useState<MemberActionsEnum | ''>(
    '',
  );

  const onError = (error: ApolloError) => {
    injectAutoHideToastOfType(
      'members-page-toast',
      SOMETHING_WENT_WRONG_MESSAGE,
      ToastType.ERROR,
    );
    console.log(error);
  };

  const [disableUserMutation] = useMutation(DISABLE_USER, {
    onCompleted: () => {
      setActive(false);
      injectAutoHideToastOfType(
        MEMBER_PAGE_TOAST_ID,
        MEMBER_DISABLED_SUCCESS_MESSAGE,
        ToastType.SUCCESS,
      );
    },
    onError,
  });

  const [enableUserMutation] = useMutation(ENABLE_USER, {
    onCompleted: () => {
      setActive(true);
      injectAutoHideToastOfType(
        MEMBER_PAGE_TOAST_ID,
        MEMBER_ENABLED_SUCCESS_MESSAGE,
        ToastType.SUCCESS,
      );
    },
    onError,
  });

  const defaultOptions = {
    variables: {
      id: memberId,
    },
  };

  const modalsMap: {
    [key: string]: { title: string; message: string; action: any };
  } = {
    [MemberActionsEnum.ENABLE_USER]: {
      title: '',
      message: ENABLE_USER_CONFIRMATION,
      action: () => {
        enableUserMutation(defaultOptions);
        setSelectedAction('');
      },
    },
    [MemberActionsEnum.DISABLE_USER]: {
      title: DISABLE_USER_CONFIRMATION_TITLE,
      message: DISABLE_USER_CONFIRMATION_DESCRIPTION.replace(
        ':username',
        name || firstName || '',
      ),
      action: () => {
        disableUserMutation(defaultOptions);
        setSelectedAction('');
      },
    },
  };

  const getDialogData = () => {
    return modalsMap[selectedAction]
      ? { ...modalsMap[selectedAction], isOpen: true }
      : {
          title: '',
          message: '',
          action: () => {},
          isOpen: false,
        };
  };

  return {
    dialogData: getDialogData(),
    setSelectedAction,
  };
};

export default useMemberActions;
